import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShoppingCart, 
  X, 
  Trash2, 
  ChevronDown, 
  Package, 
  Image,
  Loader2,
  CreditCard
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useCart } from "../contexts/CartContext";

const FloatingCart = ({ 
  galleryId, 
  galleryTitle,
  packagePrice,
  allowPackage = false,
  photoCount = 0,
  apiBaseUrl = API
}) => {
  // Use cart from context
  const { cart, removeFromCart, clearCart: contextClearCart, getCartTotal } = useCart();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [checkoutMode, setCheckoutMode] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseType, setPurchaseType] = useState("cart"); // "cart" or "package"
  const [customerInfo, setCustomerInfo] = useState({
    email: "",
    name: ""
  });

  // Track previous cart length to detect additions
  const prevCartLengthRef = useRef(0);
  
  // Auto-expand when items are added
  useEffect(() => {
    const wasAdded = cart.length > prevCartLengthRef.current && cart.length > 0;
    prevCartLengthRef.current = cart.length;
    
    if (wasAdded) {
      setIsMinimized(false);
      
      // Auto-collapse after 3 seconds if not opened
      const timer = setTimeout(() => {
        if (!isOpen) setIsMinimized(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [cart.length, isOpen]);

  const clearCart = () => {
    contextClearCart();
    toast.success("Cart cleared");
  };

  const handleCheckout = async () => {
    if (!customerInfo.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    if (purchaseType === "cart" && cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setPurchasing(true);
    try {
      let response;
      
      if (purchaseType === "package") {
        // Package checkout
        response = await axios.post(`${apiBaseUrl}/downloads/checkout/package`, {
          gallery_id: galleryId,
          origin_url: window.location.origin,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name || undefined
        });
      } else {
        // Cart checkout
        response = await axios.post(`${apiBaseUrl}/downloads/checkout/cart`, {
          items: cart.map(item => ({
            asset_id: item.asset_id,
            gallery_id: galleryId
          })),
          origin_url: window.location.origin,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name || undefined
        });
      }

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success("Redirecting to checkout...");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to process checkout";
      toast.error(message);
      setPurchasing(false);
    }
  };

  // Calculate savings for package
  const packageSavings = allowPackage && packagePrice 
    ? Math.max(0, (photoCount * (cart[0]?.price || 5)) - packagePrice)
    : 0;

  // Don't render if no purchase options
  if (!allowPackage && cart.length === 0) {
    return null;
  }

  return (
    <>
      {/* Floating Cart Button - Top Right to avoid toast overlap */}
      <AnimatePresence>
        {(cart.length > 0 || allowPackage) && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-20 right-6 z-50"
            data-testid="floating-cart"
          >
            {/* Minimized View */}
            {isMinimized ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMinimized(false)}
                className="relative bg-primary text-primary-foreground p-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                data-testid="cart-fab"
              >
                <ShoppingCart className="w-6 h-6" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-white text-primary text-xs font-bold rounded-full flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </motion.button>
            ) : (
              /* Expanded Cart Panel */
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-card border border-border rounded-2xl shadow-2xl w-[340px] max-h-[70vh] flex flex-col overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-primary" />
                    <span className="font-medium">
                      {checkoutMode ? "Checkout" : "Your Cart"}
                    </span>
                    {cart.length > 0 && !checkoutMode && (
                      <Badge variant="secondary">{cart.length}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!checkoutMode && cart.length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={clearCart}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        if (checkoutMode) {
                          setCheckoutMode(false);
                        } else {
                          setIsMinimized(true);
                        }
                      }}
                    >
                      {checkoutMode ? (
                        <ChevronDown className="w-4 h-4 rotate-180" />
                      ) : (
                        <ChevronDown className="w-4 h-4 rotate-180" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Content */}
                {checkoutMode ? (
                  /* Checkout Form */
                  <div className="p-4 space-y-4">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {purchaseType === "package" 
                            ? `All ${photoCount} photos` 
                            : `${cart.length} photo${cart.length !== 1 ? 's' : ''}`
                          }
                        </span>
                        <span className="font-serif text-lg">
                          ${purchaseType === "package" ? packagePrice?.toFixed(2) : getCartTotal().toFixed(2)}
                        </span>
                      </div>
                      {purchaseType === "package" && packageSavings > 0 && (
                        <p className="text-xs text-green-400 mt-1">
                          You save ${packageSavings.toFixed(2)} with the package!
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Email *</Label>
                        <Input
                          type="email"
                          value={customerInfo.email}
                          onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="your@email.com"
                          className="mt-1"
                          data-testid="checkout-email-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Name (optional)</Label>
                        <Input
                          value={customerInfo.name}
                          onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Your name"
                          className="mt-1"
                          data-testid="checkout-name-input"
                        />
                      </div>
                    </div>

                    <Button 
                      className="w-full" 
                      onClick={handleCheckout}
                      disabled={purchasing || !customerInfo.email.trim()}
                      data-testid="complete-purchase-btn"
                    >
                      {purchasing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      {purchasing ? "Processing..." : "Complete Purchase"}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      Secure checkout powered by Stripe
                    </p>
                  </div>
                ) : (
                  /* Cart Items */
                  <>
                    {/* Package Option */}
                    {allowPackage && packagePrice && (
                      <div className="p-4 border-b border-border">
                        <button
                          onClick={() => {
                            setPurchaseType("package");
                            setCheckoutMode(true);
                          }}
                          className="w-full p-3 bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors text-left"
                          data-testid="buy-all-btn"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/20 rounded-lg">
                              <Package className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">Buy All Photos</p>
                              <p className="text-xs text-muted-foreground">
                                {photoCount} photos included
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-serif text-lg">${packagePrice.toFixed(2)}</p>
                              {packageSavings > 0 && (
                                <p className="text-xs text-green-400">Save ${packageSavings.toFixed(2)}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                    )}

                    {/* Cart Items List */}
                    {cart.length === 0 ? (
                      <div className="p-8 text-center">
                        <Image className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                        <p className="text-sm text-muted-foreground">
                          {allowPackage 
                            ? "Add individual photos or buy all" 
                            : "Add photos to your cart"
                          }
                        </p>
                      </div>
                    ) : (
                      <>
                        <ScrollArea className="flex-1 max-h-[250px]">
                          <div className="p-3 space-y-2">
                            {cart.map((item) => (
                              <div 
                                key={item.asset_id}
                                className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg"
                              >
                                <div className="w-12 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                                  <img
                                    src={item.thumbnail_path 
                                      ? `${apiBaseUrl.replace('/api', '')}${item.thumbnail_path}`
                                      : `${apiBaseUrl}/media/${galleryId}/${item.filename}`
                                    }
                                    alt="Cart item"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs truncate text-muted-foreground">
                                    {item.filename}
                                  </p>
                                  <p className="font-medium">${item.price?.toFixed(2)}</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={() => removeFromCart(item.asset_id)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>

                        {/* Cart Footer */}
                        <div className="p-4 border-t border-border space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-serif text-xl">${getCartTotal().toFixed(2)}</span>
                          </div>
                          <Button 
                            className="w-full"
                            onClick={() => {
                              setPurchaseType("cart");
                              setCheckoutMode(true);
                            }}
                            data-testid="checkout-btn"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Checkout ({cart.length})
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingCart;
